import { useQuery } from "@tanstack/react-query";
import { AddressKeys } from "./address.keys";
import { useApi } from "../../api/ApiProvider";
import { AppointmentCoordinatesDto, ClosestAppointmentDto, ClosestContinuousAppointmentDto, PatientCoordinatesDto, SublocalityDto } from "../../api-client";
import { useLocationId } from "../User";

export const useAllSublocalities = (enabled: boolean = true) => {
    const { address: addressApi } = useApi();
    const locationId = useLocationId();

    return useQuery<SublocalityDto[]>({
        queryKey: AddressKeys.sublocalities.all(locationId),
        queryFn: async () => addressApi.getAllSublocalities(locationId!),
        enabled: enabled && !!locationId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        meta: { errorMessage: "Stadtteile konnten nicht geladen werden" },
    });
}

export const usePatientCoordinates = (enabled: boolean = true) => {
    const { address: addressApi } = useApi();
    const locationId = useLocationId();

    return useQuery<PatientCoordinatesDto[]>({
        queryKey: AddressKeys.patientCoordinates.all(locationId),
        queryFn: async () => addressApi.getPatientsCoordinates(locationId!),
        enabled: enabled && !!locationId,
        staleTime: 3 * 60 * 1000, // 3 minutes
        meta: { errorMessage: "Patientenkoordinaten konnten nicht geladen werden" },
    });
}

export const useGetGeojsonSublocalities = (enabled: boolean = false) => {
    const { address: addressApi } = useApi();
    const locationId = useLocationId();

    return useQuery<object>({
        queryKey: AddressKeys.geojson.all(locationId),
        queryFn: async () => addressApi.getGeojsonSublocalities(locationId!),
        enabled: enabled && !!locationId,
        meta: { errorMessage: "Geojson Stadtteile konnten nicht geladen werden" },
    });
}

export const useAppointmentCoordinatesBatch = (
    appointmentIds: string[],
    enabled: boolean = true,
) => {
    const { address: addressApi } = useApi();

    return useQuery<AppointmentCoordinatesDto[]>({
        queryKey: AddressKeys.appointmentCoordinates.byIds(appointmentIds),
        queryFn: async () => addressApi.getAppointmentCoordinatesBatch(appointmentIds),
        enabled: enabled && appointmentIds.length > 0,
        staleTime: 5 * 60 * 1000, // 5 minutes
        meta: { errorMessage: "Terminkoordinaten konnten nicht geladen werden" },
    });
};

export const useClosestAppointmentsByWeek = (
    appointmentId: string | null | undefined,
    addressId: string | null | undefined,
    weekStartDate: string | undefined,
    selectedDate: string | undefined,
    enabled: boolean = true,
) => {
    const { address: addressApi } = useApi();
    const locationId = useLocationId();

    return useQuery<ClosestAppointmentDto[]>({
        queryKey: AddressKeys.closestAppointments.byWeek(
            appointmentId ?? addressId ?? "",
            weekStartDate ?? "",
            selectedDate ?? "",
            locationId,
        ),
        queryFn: async () =>
            addressApi.getClosestAppointmentsByWeek(locationId!, {
                appointmentId: appointmentId ?? undefined,
                addressId: addressId ?? undefined,
                weekStartDate: weekStartDate!,
                selectedDate: selectedDate ?? undefined,
            }),
        enabled:
            enabled &&
            !!locationId &&
            (!!appointmentId || !!addressId) &&
            !!weekStartDate,
        staleTime: 2 * 60 * 1000, // 2 minutes
        meta: { errorMessage: "Nächstgelegene Termine konnten nicht geladen werden" },
    });
};

/**
 * The Dauertermin plane's twin of `useClosestAppointmentsByWeek`.
 *
 * The continuous calendar has no dates, so there is no week to scope by: results
 * cover every weekday and `dayOfWeek` only tells the server which weekday the
 * user is currently looking at, so those rows get real travel times too.
 */
export const useClosestContinuousAppointments = (
    continuousAppointmentId: string | null | undefined,
    addressId: string | null | undefined,
    dayOfWeek: number | undefined,
    enabled: boolean = true,
) => {
    const { address: addressApi } = useApi();
    const locationId = useLocationId();

    return useQuery<ClosestContinuousAppointmentDto[]>({
        queryKey: AddressKeys.closestContinuousAppointments.byDay(
            continuousAppointmentId ?? addressId ?? "",
            dayOfWeek,
            locationId,
        ),
        queryFn: async () =>
            addressApi.getClosestContinuousAppointments(locationId!, {
                continuousAppointmentId: continuousAppointmentId ?? undefined,
                addressId: addressId ?? undefined,
                dayOfWeek,
            }),
        enabled:
            enabled &&
            !!locationId &&
            (!!continuousAppointmentId || !!addressId),
        staleTime: 2 * 60 * 1000, // 2 minutes
        meta: { errorMessage: "Nächstgelegene Dauertermine konnten nicht geladen werden" },
    });
};